import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Moon, Sun, Menu, X, LogIn, LogOut, User, Settings, FileText, Download } from "lucide-react";
import { useLanguage, type Language } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import FlagIcon from "@/components/FlagIcon";
import { ProfileEdit } from "@/components/ProfileEdit";
import { UserManagement } from "@/components/UserManagement";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const { language, setLanguage, t } = useLanguage();
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [userManagementKey, setUserManagementKey] = useState(0);
  const [userProfile, setUserProfile] = useState<{ full_name?: string; username?: string } | null>(null);

  const languages = [
    { code: "en", name: "English" },
    { code: "id", name: "Indonesia" },
    { code: "ja", name: "Japan" },
    { code: "ko", name: "Korea" },
    { code: "zh", name: "China" },
    { code: "de", name: "Germany" },
    { code: "fr", name: "France" },
    { code: "es", name: "Spain" },
  ];

  const navLinks = [
    { title: t.nav.whyme, href: "#whyme" },
    { title: t.nav.experience, href: "#experience" },
    { title: t.nav.portfolio_link, href: "#portfolio" },
    { title: t.nav.education, href: "#education" },
    { title: t.nav.organizational, href: "#organizational" },
    { title: t.nav.events, href: "#events" },
    { title: t.nav.skills, href: "#skills" },
    { title: t.nav.awards, href: "#awards" },
    { title: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Scroll spy logic
      const sections = navLinks.map(link => link.href.substring(1));
      const scrollPosition = window.scrollY + 100;

      let found = false;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          found = true;
          break;
        }
      }
      if (!found) setActiveSection("");
    };
    
    handleScroll(); // Initial check
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('full_name, username')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setUserProfile(data);
        }
      } else {
        setUserProfile(null);
      }
    };

    fetchProfile();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen) {
        const target = event.target as HTMLElement;
        const navbar = document.querySelector('nav');
        if (navbar && !navbar.contains(target)) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const toggleDarkMode = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle("dark");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-4 pt-3 sm:pt-4 transition-all duration-500">
      <div
        className={`glass-panel glass-sheen mx-auto max-w-7xl rounded-2xl transition-all duration-500 ${
          isScrolled ? "shadow-2xl" : ""
        }`}
      >
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <a
              href="#hero"
              className="text-xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent hover:opacity-90 transition-opacity tracking-tight"
            >
              {t.nav.portfolio}
            </a>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-0.5">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all duration-300 ${
                    activeSection === link.href.substring(1)
                      ? "bg-white/40 dark:bg-white/10 text-primary shadow-[inset_0_1px_0_0_rgba(255,255,255,0.4)] ring-1 ring-primary/30"
                      : "text-foreground/80 hover:text-primary hover:bg-white/30 dark:hover:bg-white/5"
                  }`}
                >
                  {link.title}
                </a>
              ))}
            </div>

            {/* Desktop Actions */}
            <div className="flex items-center gap-2">
              {user ? (
                <div className="hidden md:flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="glass" size="sm" className="gap-2 rounded-full">
                        <User className="h-4 w-4" />
                        <span className="text-xs">
                          {userProfile?.username || userProfile?.full_name || user.email?.split('@')[0]}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="glass-panel border-0">
                      <DropdownMenuItem onClick={() => setShowProfileEdit(true)}>
                        <Settings className="mr-2 h-4 w-4" />
                        Edit Profile
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => {
                            setUserManagementKey(prev => prev + 1);
                            setShowUserManagement(true);
                          }}>
                            <User className="mr-2 h-4 w-4" />
                            User Management
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => signOut()}
                    className="gap-2 rounded-full"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </Button>
                </div>
              ) : (
                <Button
                  variant="glass-primary"
                  size="sm"
                  onClick={() => navigate('/auth')}
                  className="hidden md:flex gap-2 rounded-full"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="glass" size="sm" className="rounded-full gap-2">
                    <FlagIcon code={language} className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase">{language}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="glass-panel border-0 z-50">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setLanguage(lang.code as Language)}
                      className={language === lang.code ? "bg-primary/10" : ""}
                    >
                      <FlagIcon code={lang.code} className="mr-2 h-4 w-4" />
                      <span className="uppercase text-xs font-semibold mr-2">{lang.code}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="glass"
                size="icon"
                onClick={toggleDarkMode}
                className="rounded-full h-9 w-9"
                aria-label="Toggle dark mode"
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <Button
                variant="glass"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="xl:hidden rounded-full h-9 w-9"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

      {/* Menu Sidebar */}
      {isMobileMenuOpen && (
        <div className="border-t border-white/20 dark:border-white/10 animate-fade-up rounded-b-2xl">
          <div className="px-4 py-6 flex flex-col items-center gap-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full text-base font-medium text-foreground hover:text-primary hover:bg-white/30 dark:hover:bg-white/5 rounded-full transition-colors py-2 text-center"
              >
                {link.title}
              </a>
            ))}
            <div className="flex flex-col items-center gap-3 pt-3 mt-2 border-t border-white/20 dark:border-white/10 w-full max-w-xs">
              {user ? (
                <>
                  <div className="flex items-center gap-2 py-2">
                    <User className="h-4 w-4" />
                    <span className="text-sm">
                      {userProfile?.username || userProfile?.full_name || user.email}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="glass"
                    onClick={() => {
                      setShowProfileEdit(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full gap-2 rounded-full"
                  >
                    <Settings className="h-4 w-4" />
                    Edit Profile
                  </Button>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="glass"
                      onClick={() => {
                        setUserManagementKey(prev => prev + 1);
                        setShowUserManagement(true);
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full gap-2 rounded-full"
                    >
                      <User className="h-4 w-4" />
                      User Management
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="glass"
                    onClick={() => {
                      signOut();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full gap-2 rounded-full"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="glass-primary"
                  onClick={() => {
                    navigate('/auth');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full gap-2 rounded-full"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </Button>
              )}
              <Button size="sm" variant="glass-primary" asChild className="w-full gap-2 rounded-full">
                <a href="/Widyo_TCU_Data_DevOps.pdf" download="Widyo_Tri_Cahya_Utomo_CV.pdf" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)}>
                  <Download className="h-4 w-4" />
                  {t.nav.downloadResume}
                </a>
              </Button>
              <Button size="sm" variant="glass" asChild className="w-full gap-2 rounded-full">
                <a href="https://bit.ly/widyo_portfolio" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)}>
                  <FileText className="h-4 w-4" />
                  {t.nav.downloadPortfolio}
                </a>
              </Button>
              <Button variant="glass" size="sm" asChild className="w-full rounded-full">
                <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>
                  {t.nav.letsConnect}
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>



      {/* Profile Edit Dialog */}
      <ProfileEdit open={showProfileEdit} onOpenChange={setShowProfileEdit} />

      {/* User Management Dialog */}
      <Dialog open={showUserManagement} onOpenChange={setShowUserManagement}>
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh] overflow-y-auto p-0">
          <div className="sticky top-0 z-10 bg-background border-b px-6 py-4 flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold">User Management</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowUserManagement(false)}
              className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="px-6 pb-6">
            <UserManagement key={userManagementKey} />
          </div>
        </DialogContent>
      </Dialog>
    </nav>
  );
};

export default Navbar;
